/**
 * [INPUT]: 依赖浏览器 localStorage、真实 server 管理员用户名密码 Authorization 会话与统一 API 基地址
 * [OUTPUT]: 对外提供真实管理员用户名密码登录、退出、跨 Tab 同步与路由守卫能力
 * [POS]: admin-web 身份上下文，只接受 server 返回的 admin 角色
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useEffect } from 'react';
import { parseAuthTokens } from '@elseyou/api-client';
import { apiBaseUrl } from '../lib/api-config';
import { ADMIN_AUTH_EVENT, clearAdminAuthTokens, getAdminAuthTokens, saveAdminAuthTokens } from '../lib/auth-tokens';

const SESSION_KEY = 'elseyou.admin.session';
const LEGACY_SESSION_KEY = 'lifeverse.admin.session';

export interface AdminSession {
  id: string;
  name: string;
  email: string;
  role: 'admin';
}

interface AuthContextValue {
  session: AdminSession | null;
  login: (identifier: string, secret: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthUserResponse = {
  id?: string;
  phone?: string;
  username?: string;
  display_name?: string;
  role?: string;
};

function storedAdminSession(): AdminSession | null {
  if (!getAdminAuthTokens()) return null;
  const stored = localStorage.getItem(SESSION_KEY) ?? localStorage.getItem(LEGACY_SESSION_KEY);
  if (!stored) return null;
  try {
    const candidate = JSON.parse(stored) as Partial<AdminSession>;
    if (candidate.role !== 'admin' || !candidate.id || !candidate.email) return null;
    if (!localStorage.getItem(SESSION_KEY)) localStorage.setItem(SESSION_KEY, JSON.stringify(candidate));
    localStorage.removeItem(LEGACY_SESSION_KEY);
    return candidate as AdminSession;
  } catch {
    return null;
  }
}

function removeStoredAdminSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LEGACY_SESSION_KEY);
}

async function apiRequest(path: string, init: RequestInit): Promise<Record<string, unknown>> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  headers.set('content-type', 'application/json');
  headers.set('X-Elseyou-Client', 'admin');
  const accessToken = getAdminAuthTokens()?.accessToken;
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: 'omit',
    headers,
  });
  const payload = response.status === 204 ? {} : await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(typeof payload.message === 'string' ? payload.message : `认证请求失败（${response.status}）`);
  }
  return payload;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(() => storedAdminSession());

  useEffect(() => {
    const syncSession = () => setSession(storedAdminSession());
    window.addEventListener(ADMIN_AUTH_EVENT, syncSession);
    window.addEventListener('storage', syncSession);
    return () => {
      window.removeEventListener(ADMIN_AUTH_EVENT, syncSession);
      window.removeEventListener('storage', syncSession);
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    async login(identifier, secret) {
      const username = identifier.trim();
      if (!username || !secret) throw new Error('请输入管理员用户名和密码。');
      const payload = await apiRequest('/auth/admin/login', { method: 'POST', body: JSON.stringify({ username, password: secret }) });
      const tokens = parseAuthTokens(payload);
      if (!tokens) throw new Error('登录响应缺少 Authorization token。');
      const user = (payload.user ?? {}) as AuthUserResponse;
      if (user.role !== 'admin' || !user.id) {
        await apiRequest('/auth/logout', { method: 'POST', body: JSON.stringify({ refresh_token: tokens.refreshToken }) }).catch(() => undefined);
        throw new Error('该账号没有管理员权限。');
      }
      const next: AdminSession = { id: user.id, name: user.display_name || '管理员', email: user.username || username, role: 'admin' };
      saveAdminAuthTokens(tokens);
      localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      localStorage.removeItem(LEGACY_SESSION_KEY);
      setSession(next);
    },
    logout() {
      const refreshToken = getAdminAuthTokens()?.refreshToken;
      void apiRequest('/auth/logout', { method: 'POST', body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}) }).catch(() => undefined);
      const cleared = clearAdminAuthTokens(refreshToken ?? undefined);
      if (cleared) {
        removeStoredAdminSession();
        setSession(null);
      }
    },
  }), [session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth 必须在 AuthProvider 内使用。');
  return context;
}

export function isAdminSession(session: AdminSession | null): session is AdminSession {
  return session?.role === 'admin';
}
